<template>
  <ContextMenu.Root>
    <ContextMenu.Trigger>
      <tr
        :ref="handleRef"
        :data-index="index"
        :class="[
          `group relative transition-colors contain-content`,
          highlightedIds?.has(row.original.id)
            ? 'bg-blue-50 dark:bg-blue-950'
            : !isSelected
            ? !isActive
              ? `hover:bg-muted`
              : `bg-muted`
            : `bg-blue-100 hover:bg-blue-200 dark:bg-muted dark:hover:!bg-blue-900`,
          customRowClass,
        ]"
      >
        <td
          v-for="cell in row.getVisibleCells()"
          :key="cell.id"
          :class="[
            `truncate whitespace-nowrap px-2 tabular-nums leading-6 first:pl-4 last:pr-4`,
            isPrimaryColumn(cell.column)
              ? `w-full min-w-80 max-w-0 font-bold`
              : `lg:[&:nth-last-child(2)]:text-right`,
            cell.column.columnDef.meta?.cellClass,
          ]"
          @click="handleCellClick(cell, $event)"
        >
          <FlexRender
            :render="cell.column.columnDef.cell"
            :props="cell.getContext()"
          />
        </td>

        <DropIndicator :edge="draggable.edge" />

        <Teleport
          v-if="draggable.previewContainer"
          :to="draggable.previewContainer"
        >
          <slot name="dragPreview" :item="row.original" />
        </Teleport>
      </tr>
    </ContextMenu.Trigger>

    <ContextMenu.Container v-if="contextMenuActions.length > 0">
      <template v-for="(item, idx) in contextMenuActions" :key="idx">
        <ContextMenu.Item
          v-if="item.type === 'action'"
          :icon="item.icon"
          :label="item.label"
          :variant="item.variant"
          @click="item.onClick"
        />
        <ContextMenu.RouteItem
          v-else-if="item.type === 'route'"
          :icon="item.icon"
          :label="item.label"
          :variant="item.variant"
          :to="item.to"
        />
        <ContextMenu.LinkItem
          v-else-if="item.type === 'link'"
          :icon="item.icon"
          :label="item.label"
          :href="item.href"
        />
        <ContextMenu.Divider v-else-if="item.type === 'divider'" />
      </template>
    </ContextMenu.Container>
  </ContextMenu.Root>
</template>

<script setup lang="ts" generic="Model extends GenericModel">
import type { Cell, Row } from "@tanstack/vue-table";
import { FlexRender } from "@tanstack/vue-table";
import { computed, ref } from "vue";

import ContextMenu from "@/design_system/ContextMenu";
import { useReorderableListItem } from "@/lib/pragmatic-dnd";
import DropIndicator from "@/lib/pragmatic-dnd/DropIndicator.vue";
import type { InternalTableInstance } from "@/lib/tanstack-table";
import { isPrimaryColumn } from "@/lib/tanstack-table";
import { ACTIONS_COLUMN_ID } from "@/lib/tanstack-table/columns";
import type { ActionItem } from "@/lib/tanstack-table/types/overflow";
import type { GenericModel } from "@/types/model";
import { isRowSelected, toggleRowSelection } from "@/types/selection";

const props = defineProps<{
  table: InternalTableInstance<Model>;
  row: Row<Model>;
  index: number;
  onMeasure: (node: any) => void;
}>();

const slots = defineSlots<{
  dragPreview?(props: { item: Model }): unknown;
}>();

const elementRef = ref<HTMLTableRowElement>();
const onMeasure = props.onMeasure;
const handleRef = (node: any) => {
  elementRef.value = node;
  onMeasure(node);
};

const draggable = useReorderableListItem({
  element: elementRef,
  renderPreview: computed(() => slots.dragPreview !== undefined),
  // `props.row.index` is the actual index of the item
  // `props.index` has group headers
  index: () => props.row.index,
});

const isActive = computed(() => {
  const table = props.table.instance;
  const activeRow = table.options.meta?.activeRow;
  if (!activeRow) {
    return false;
  }

  return activeRow.value === props.row.original.id;
});

const isSelected = computed(() => {
  const table = props.table.instance;
  const selection = table.options.meta?.selection;
  if (!selection) {
    return false;
  }

  return isRowSelected(selection.state.value, props.row.original);
});

const table = props.table.instance;
const selection = table.options.meta?.selection;
const highlightedIds = selection?.highlightedIds;
const getRowClass = table.options.meta?.getRowClass;

const customRowClass = computed(() => {
  return getRowClass?.(props.row.original);
});

const contextMenuActions = computed((): ActionItem[] => {
  const actionsColumn = table.getColumn(ACTIONS_COLUMN_ID);
  const registrar = actionsColumn?.columnDef.meta?.actionsRegistrar;
  if (!registrar) {
    return [];
  }

  const actions = registrar({
    item: props.row.original,
    index: props.row.index,
  });

  return actions.filter((action) => action.type !== "submenu");
});

const handleCellClick = (cell: Cell<Model, unknown>, event: MouseEvent) => {
  // If the user is clicking on a button or a link, back out
  if ((event.target as HTMLElement).closest("button, a[href]")) {
    return;
  }

  if (isPrimaryColumn(cell.column)) {
    const table = props.table.instance;

    const selection = table.options.meta?.selection;
    if (!selection) {
      return;
    }

    const state = selection.state;

    const item = cell.row.original;
    const isSelectable = selection.canSelect?.(item, state.value) ?? true;

    if (isSelected.value || isSelectable) {
      toggleRowSelection(state, item, !isSelected.value);
    }
  }
};
</script>
